import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import HowItWorks from "@/components/HowItWorks";
import CategoryGrid from "@/components/CategoryGrid";
import CTASection from "@/components/CTASection";
import Footer from "@/components/Footer";
import MobileFooter from "@/components/MobileFooter";
import AnnoncePreviewSection from "@/components/AnnoncePreviewSection";
import ClientHomeContent from "@/components/ClientHomeContent";
import ClientBottomNav from "@/components/ClientBottomNav";
import { useAuth } from "@/contexts/AuthContext";
import { useIsArtisan } from "@/hooks/useArtisanProfile";
import { useEffect } from "react";
import { useSEO, useJsonLd } from "@/hooks/useSEO";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";
import { useLanguage } from "@/hooks/useLanguage";

const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Hirafi",
  url: "https://www.hirafidz.com",
  inLanguage: "fr-DZ",
  description:
    "Plateforme de mise en relation entre artisans qualifiés et particuliers en Algérie",
  potentialAction: {
    "@type": "SearchAction",
    target: "https://www.hirafidz.com/recherche-artisans?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

const LOCALBUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "OnlineBusiness",
  name: "Hirafi",
  url: "https://www.hirafidz.com",
  logo: "https://www.hirafidz.com/og-image.png",
  image: "https://www.hirafidz.com/og-image.png",
  description:
    "Plateforme algérienne de mise en relation entre artisans qualifiés et particuliers dans toutes les wilayas d'Algérie.",
  areaServed: { "@type": "Country", name: "Algeria", addressCountry: "DZ" },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Métiers disponibles",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Plomberie" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Électricité" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Serrurerie" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Menuiserie" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Peinture" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Maçonnerie" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Carrelage" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Climatisation" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Artisan polyvalent" } },
    ],
  },
  contactPoint: {
    "@type": "ContactPoint",
    email: "contact@hirafidz.com",
    contactType: "customer service",
    availableLanguage: ["French", "Arabic"],
  },
  sameAs: [],
};

const Index = () => {
  const { user } = useAuth();
  const { data: isArtisan, isLoading: artisanLoading } = useIsArtisan();
  const lNavigate = useLocalizedNavigate();
  const { locale } = useLanguage();

  useSEO({
    title: "Trouvez un artisan de confiance en Algérie",
    description: locale === "ar"
      ? "هل تحتاج إلى سبّاك أو كهربائي أو بنّاء في الجزائر؟ انشر طلبك مجانًا واحصل على عروض من حرفيين موثّقين في جميع ولايات الجزائر."
      : "Besoin d'un plombier, électricien ou maçon en Algérie ? Publiez votre demande gratuitement et recevez des propositions d'artisans qualifiés dans toutes les wilayas d'Algérie.",
    canonical: "https://www.hirafidz.com/",
    locale,
  });
  useJsonLd(WEBSITE_JSONLD, "website");
  useJsonLd(LOCALBUSINESS_JSONLD, "localbusiness");

  // Artisan connecté → toujours rediriger vers le dashboard
  useEffect(() => {
    if (!artisanLoading && user && isArtisan) {
      lNavigate("/dashboard", { replace: true });
    }
  }, [user, isArtisan, artisanLoading]);

  // Attendre la résolution du type d'utilisateur avant de router
  if (user && artisanLoading) return null;

  // Client connecté → écran d'accueil client
  if (user && !isArtisan) {
    return (
      <div className="min-h-screen pb-20 md:pb-0">
        <Navbar />
        <main>
          <ClientHomeContent />
        </main>
        <div className="hidden md:block"><Footer /></div>
        <ClientBottomNav />
      </div>
    );
  }

  // Non connecté → page publique
  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        <HeroSection />
        <HowItWorks />
        <AnnoncePreviewSection />
        <CategoryGrid />
        <CTASection />
      </main>
      <div className="hidden md:block"><Footer /></div>
      <MobileFooter />
    </div>
  );
};

export default Index;
